import { Cloud, Wrench, HeadphonesIcon, Sparkles } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

export type WorkflowCategory = 'Cloud' | 'DevOps' | 'Support' | 'AI';

export const CATEGORY_ORDER: WorkflowCategory[] = ['Cloud', 'DevOps', 'Support', 'AI'];

export const CATEGORY_CONFIG: Record<WorkflowCategory, {
  icon: LucideIcon;
  color: string;
  borderColor: string;
}> = {
  Cloud: {
    icon: Cloud,
    color: 'text-sky-600 dark:text-sky-400',
    borderColor: 'border-sky-200 dark:border-sky-800',
  },
  DevOps: {
    icon: Wrench,
    color: 'text-purple-600 dark:text-purple-400',
    borderColor: 'border-purple-200 dark:border-purple-800',
  },
  Support: {
    icon: HeadphonesIcon,
    color: 'text-amber-600 dark:text-amber-400',
    borderColor: 'border-amber-200 dark:border-amber-800',
  },
  AI: {
    icon: Sparkles,
    color: 'text-emerald-600 dark:text-emerald-400',
    borderColor: 'border-emerald-200 dark:border-emerald-800',
  },
};

export function resolveWorkflowCategory(workflowName: string, existingCategory?: string): WorkflowCategory {
  // If the category from the YAML matches a known rendered category, use it directly.
  if (existingCategory && existingCategory in CATEGORY_CONFIG) {
    return existingCategory as WorkflowCategory;
  }
  // Name-pattern fallback for workflows without a category in the YAML.
  const name = workflowName.toLowerCase();
  if (name.includes('service-report')) return 'Support';
  if (name.includes('mas') || name.includes('cloud')) return 'Cloud';
  return 'DevOps';
}

/**
 * Extract the workflow template name from a run record.
 * Prefers the explicit `templateName` field (new records).
 * Falls back to stripping the UUID-based run suffix from the Argo instance name
 * for legacy records where workflowName was stored as the full Argo instance name.
 */
export function getRunTemplateName(run: { workflowName: string; templateName?: string }): string {
  if (run.templateName) return run.templateName;
  // Argo instance names follow the pattern: {template}-{uuid}-{suffix}
  // where uuid is a v4 UUID (8-4-4-4-12 hex chars). Strip from the first UUID segment.
  const uuidPattern = /-[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;
  const match = run.workflowName.match(uuidPattern);
  if (match) return run.workflowName.slice(0, match.index);
  return run.workflowName;
}

export function groupByCategory<T>(
  items: T[],
  getName: (item: T) => string,
  getCategory?: (item: T) => string | undefined,
): Map<WorkflowCategory, T[]> {
  const map = new Map<WorkflowCategory, T[]>();
  for (const cat of CATEGORY_ORDER) {
    map.set(cat, []);
  }
  for (const item of items) {
    const cat = resolveWorkflowCategory(getName(item), getCategory?.(item));
    map.get(cat)!.push(item);
  }
  return map;
}
